import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, ChevronDown, ChevronRight, Tag } from 'lucide-react';
import api, { isAuthenticated } from '../utils/api';
import { toast } from 'sonner';

const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSubForm, setShowSubForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [activeCategoryForSub, setActiveCategoryForSub] = useState(null);
  const [formData, setFormData] = useState({
    name: '', slug: '', image: '', description: '', visible: true
  });
  const [subFormData, setSubFormData] = useState({
    name: '', slug: '', category_slug: '', image: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/admin/login'); return; }
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const r = await api.get('/categories');
      setCategories(r.data);
    } catch (e) {}
  };

  const fetchSubcategories = async (categorySlug) => {
    try {
      const r = await api.get(`/subcategories?category_slug=${categorySlug}`);
      setSubcategories(prev => ({ ...prev, [categorySlug]: r.data }));
    } catch (e) {}
  };

  const toggleExpand = (slug) => {
    if (expandedCategory === slug) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(slug);
      if (!subcategories[slug]) fetchSubcategories(slug);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/categories/${editing.slug}`, formData);
      else await api.post('/categories', formData);
      toast.success('Saved!');
      setShowForm(false);
      setEditing(null);
      setFormData({ name: '', slug: '', image: '', description: '', visible: true });
      fetchCategories();
    } catch (e) { toast.error('Error saving category'); }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/categories/${slug}`);
      toast.success('Deleted');
      fetchCategories();
    } catch (e) { toast.error('Error deleting'); }
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSub) {
        await api.put(`/subcategories/${editingSub.slug}`, subFormData);
        toast.success('Subcategory updated!');
      } else {
        await api.post('/subcategories', subFormData);
        toast.success('Subcategory added!');
      }
      setShowSubForm(false);
      setEditingSub(null);
      setSubFormData({ name: '', slug: '', category_slug: '', image: '' });
      fetchSubcategories(activeCategoryForSub);
    } catch (e) { toast.error('Error saving subcategory'); }
  };

  const handleSubDelete = async (sub, categorySlug) => {
    if (!window.confirm('Delete this subcategory?')) return;
    try {
      await api.delete(`/subcategories/${sub.slug}`);
      toast.success('Subcategory deleted');
      fetchSubcategories(categorySlug);
    } catch (e) { toast.error('Error deleting subcategory'); }
  };

  const openAddSub = (categorySlug) => {
    setActiveCategoryForSub(categorySlug);
    setEditingSub(null);
    setSubFormData({ name: '', slug: '', category_slug: categorySlug, image: '' });
    setShowSubForm(true);
  };

  const openEditSub = (sub, categorySlug) => {
    setActiveCategoryForSub(categorySlug);
    setEditingSub(sub);
    setSubFormData({
      name: sub.name,
      slug: sub.slug,
      category_slug: categorySlug,
      image: sub.image || ''
    });
    setShowSubForm(true);
  };

  // Seed default Chudidhar big-size subcategories
  const seedChudidharSizes = async () => {
    const sizes = [
      { name: '3XL', slug: '3xl', category_slug: 'chudidhar', image: '' },
      { name: '4XL', slug: '4xl', category_slug: 'chudidhar', image: '' },
      { name: '5XL', slug: '5xl', category_slug: 'chudidhar', image: '' },
    ];
    try {
      for (const size of sizes) {
        await api.post('/subcategories', size);
      }
      toast.success('Big sizes added to Chudidhar!');
      fetchSubcategories('chudidhar');
      setExpandedCategory('chudidhar');
    } catch (e) {
      toast.error('Some sizes may already exist');
      fetchSubcategories('chudidhar');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-[#E5A93C]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-[#2A0000]">Category Management</h1>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditing(null);
              setFormData({ name: '', slug: '', image: '', description: '', visible: true });
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" /><span>Add Category</span>
          </button>
        </div>
      </header>

      <div className="p-6">

        {/* Category Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <h2 className="text-2xl font-bold mb-6">{editing ? 'Edit' : 'Add'} Category</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text" placeholder="Name" value={formData.name} required
                  onChange={(e) => setFormData({
                    ...formData,
                    name: e.target.value,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '-')
                  })}
                  className="w-full px-4 py-2 border rounded"
                />
                <input
                  type="text" placeholder="Slug" value={formData.slug} required
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-2 border rounded"
                />
                <input
                  type="text" placeholder="Image URL" value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  className="w-full px-4 py-2 border rounded"
                />
                <textarea
                  placeholder="Description" value={formData.description} rows={3}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded"
                />
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox" checked={formData.visible}
                    onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
                  />
                  <span>Visible</span>
                </label>
                <div className="flex space-x-2">
                  <button type="submit" className="flex-1 btn-primary">Save</button>
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 btn-outline">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Subcategory Form Modal */}
        {showSubForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <h2 className="text-2xl font-bold mb-2">{editingSub ? 'Edit' : 'Add'} Subcategory</h2>
              <p className="text-sm text-gray-500 mb-6">
                Under: <span className="font-medium text-[#2A0000]">{activeCategoryForSub}</span>
              </p>
              <form onSubmit={handleSubSubmit} className="space-y-4">
                <input
                  type="text" placeholder="Name (e.g. 3XL, 4XL, 5XL)" value={subFormData.name} required
                  onChange={(e) => setSubFormData({
                    ...subFormData,
                    name: e.target.value,
                    slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
                  })}
                  className="w-full px-4 py-2 border rounded"
                />
                <input
                  type="text" placeholder="Slug" value={subFormData.slug} required
                  onChange={(e) => setSubFormData({ ...subFormData, slug: e.target.value })}
                  className="w-full px-4 py-2 border rounded"
                />
                <input
                  type="text" placeholder="Image URL (optional)" value={subFormData.image}
                  onChange={(e) => setSubFormData({ ...subFormData, image: e.target.value })}
                  className="w-full px-4 py-2 border rounded"
                />
                <div className="flex space-x-2">
                  <button type="submit" className="flex-1 btn-primary">
                    {editingSub ? 'Update' : 'Add'} Subcategory
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowSubForm(false); setEditingSub(null); }}
                    className="flex-1 btn-outline"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Chudidhar Big Sizes Quick-Add Banner */}
        {categories.some(c => c.slug === 'chudidhar') && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-amber-900">Chudidhar — Big Sizes</p>
              <p className="text-sm text-amber-700 mt-0.5">
                Quickly add 3XL, 4XL and 5XL as subcategories under Chudidhar.
              </p>
            </div>
            <button
              onClick={seedChudidharSizes}
              className="flex items-center space-x-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded font-medium text-sm whitespace-nowrap"
            >
              <Tag className="w-4 h-4" />
              <span>Add 3XL / 4XL / 5XL</span>
            </button>
          </div>
        )}

        {/* Categories Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase w-8"></th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase">Slug</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase">Visible</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase">Subcategories</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {categories.map(cat => (
                <React.Fragment key={cat.slug}>
                  {/* Category Row */}
                  <tr className={`hover:bg-gray-50 transition-colors ${expandedCategory === cat.slug ? 'bg-amber-50/40' : ''}`}>
                    <td className="px-3 py-4">
                      <button
                        onClick={() => toggleExpand(cat.slug)}
                        className="text-gray-400 hover:text-[#E5A93C] transition-colors"
                        title="Show subcategories"
                      >
                        {expandedCategory === cat.slug
                          ? <ChevronDown className="w-4 h-4" />
                          : <ChevronRight className="w-4 h-4" />
                        }
                      </button>
                    </td>
                    <td className="px-6 py-4 font-medium text-[#2A0000]">{cat.name}</td>
                    <td className="px-6 py-4 text-gray-500 text-sm">{cat.slug}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${cat.visible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {cat.visible ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleExpand(cat.slug)}
                        className="text-xs text-[#E5A93C] hover:underline"
                      >
                        {subcategories[cat.slug]
                          ? `${subcategories[cat.slug].length} subcategories`
                          : 'View subcategories'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button onClick={() => { setEditing(cat); setFormData(cat); setShowForm(true); }}>
                          <Edit className="w-5 h-5 text-blue-600 hover:text-blue-800" />
                        </button>
                        <button onClick={() => handleDelete(cat.slug)}>
                          <Trash2 className="w-5 h-5 text-red-600 hover:text-red-800" />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Subcategory Section */}
                  {expandedCategory === cat.slug && (
                    <tr>
                      <td colSpan={6} className="px-0 py-0 bg-gray-50">
                        <div className="pl-12 pr-6 py-4 border-t border-[#E5A93C]/20">

                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <Tag className="w-4 h-4 text-[#E5A93C]" />
                              <span className="text-sm font-semibold text-[#2A0000]">
                                Subcategories of "{cat.name}"
                              </span>
                              {cat.slug === 'chudidhar' && (
                                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                  Big Sizes
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => openAddSub(cat.slug)}
                              className="flex items-center space-x-1 text-xs px-3 py-1.5 bg-[#E5A93C] text-white rounded hover:bg-[#d49830] transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                              <span>Add Subcategory</span>
                            </button>
                          </div>

                          {!subcategories[cat.slug] ? (
                            <p className="text-sm text-gray-400 py-2">Loading...</p>
                          ) : subcategories[cat.slug].length === 0 ? (
                            <div className="text-sm text-gray-400 py-3 text-center border border-dashed border-gray-300 rounded">
                              No subcategories yet.{' '}
                              {cat.slug === 'chudidhar' && (
                                <button
                                  onClick={seedChudidharSizes}
                                  className="text-[#E5A93C] hover:underline font-medium"
                                >
                                  Add big sizes (3XL, 4XL, 5XL)
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                              {subcategories[cat.slug].map(sub => (
                                <div
                                  key={sub.slug}
                                  className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2"
                                >
                                  <div className="flex items-center space-x-2">
                                    {/* Size badge styling for Chudidhar big sizes */}
                                    {cat.slug === 'chudidhar' && ['3xl', '4xl', '5xl'].includes(sub.slug) ? (
                                      <span className="px-2 py-0.5 text-xs font-bold bg-[#2A0000] text-white rounded">
                                        {sub.name}
                                      </span>
                                    ) : (
                                      <span className="w-2 h-2 rounded-full bg-[#E5A93C]" />
                                    )}
                                    <div>
                                      <p className="text-sm font-medium text-[#2C2C2C]">{sub.name}</p>
                                      <p className="text-xs text-gray-400">{sub.slug}</p>
                                    </div>
                                  </div>
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => openEditSub(sub, cat.slug)}
                                      className="p-1 hover:bg-blue-50 rounded"
                                    >
                                      <Edit className="w-3.5 h-3.5 text-blue-600" />
                                    </button>
                                    <button
                                      onClick={() => handleSubDelete(sub, cat.slug)}
                                      className="p-1 hover:bg-red-50 rounded"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-600" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminCategoriesPage;